import { createClient } from '@supabase/supabase-js';
import logger from '../utils/logger';
import { getEnv } from '../utils/envConfig';

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

class SupabaseService {
  constructor() {
    this.supabaseUrl = getEnv('REACT_APP_SUPABASE_URL', '');
    this.supabaseAnonKey = getEnv('REACT_APP_SUPABASE_ANON_KEY', '');
    this._client = null;
  }

  setConfig(config = {}) {
    const hasUrlProp = Object.prototype.hasOwnProperty.call(config, 'url') ||
      Object.prototype.hasOwnProperty.call(config, 'supabaseUrl');
    if (hasUrlProp) {
      if (Object.prototype.hasOwnProperty.call(config, 'url')) {
        this.supabaseUrl = config.url ?? '';
      } else {
        this.supabaseUrl = config.supabaseUrl ?? '';
      }
    }

    const hasAnonProp = Object.prototype.hasOwnProperty.call(config, 'anonKey') ||
      Object.prototype.hasOwnProperty.call(config, 'supabaseAnonKey');
    if (hasAnonProp) {
      if (Object.prototype.hasOwnProperty.call(config, 'anonKey')) {
        this.supabaseAnonKey = config.anonKey ?? '';
      } else {
        this.supabaseAnonKey = config.supabaseAnonKey ?? '';
      }
    }

    this._client = null;
    logger.info('Supabase configuration updated', {
      hasUrl: !!this.supabaseUrl,
      hasAnonKey: !!this.supabaseAnonKey,
    });
  }

  resetClient() {
    this._client = null;
  }

  get client() {
    if (!this._client) {
      if (!this.supabaseUrl || !this.supabaseAnonKey) {
        const error = new Error(
          'Supabase configuration missing. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in your environment.'
        );
        error.code = 'SUPABASE_CONFIG_MISSING';
        logger.error('Supabase client initialization failed', error, {
          hasUrl: !!this.supabaseUrl,
          hasAnonKey: !!this.supabaseAnonKey,
        });
        throw error;
      }

      this._client = createClient(this.supabaseUrl, this.supabaseAnonKey, {
        auth: {
          persistSession: false,
          detectSessionInUrl: false,
        },
      });

      logger.info('Supabase client initialized', {
        hasUrl: !!this.supabaseUrl,
      });
    }

    return this._client;
  }

  isRetriableError(error) {
    if (!error) {
      return false;
    }

    const status = error.status || error.__rawStatus || error.code;

    if (typeof status === 'number') {
      if (status >= 500 || status === 408 || status === 429) {
        return true;
      }
    }

    const message = (error.message || '').toLowerCase();
    if (message.includes('fetch failed') || message.includes('network') || message.includes('timeout')) {
      return true;
    }

    const code = (error.code || '').toString().toUpperCase();
    const transientCodes = ['PGRST301', 'PGRST504', 'PGRST503'];
    if (transientCodes.includes(code)) {
      return true;
    }

    return false;
  }

  async withRetry(operation, handler, attempt = 1) {
    try {
      return await handler();
    } catch (error) {
      const shouldRetry = attempt < MAX_RETRIES && this.isRetriableError(error);
      if (shouldRetry) {
        logger.warning(`Supabase ${operation} failed, retrying`, {
          attempt,
          maxRetries: MAX_RETRIES,
          error: error?.message,
        });
      } else {
        logger.error(`Supabase ${operation} failed`, error, {
          operation,
          attempt,
          maxRetries: MAX_RETRIES,
        });
      }

      if (shouldRetry) {
        await delay(RETRY_DELAY_MS * attempt);
        return this.withRetry(operation, handler, attempt + 1);
      }

      throw error;
    }
  }

  applyFilters(query, filters = []) {
    if (!filters || filters.length === 0) {
      return query;
    }

    return filters.reduce((acc, filter) => {
      const { column, operator = 'eq', value } = filter;
      switch (operator) {
        case 'eq':
          return acc.eq(column, value);
        case 'neq':
          return acc.neq(column, value);
        case 'gt':
          return acc.gt(column, value);
        case 'gte':
          return acc.gte(column, value);
        case 'lt':
          return acc.lt(column, value);
        case 'lte':
          return acc.lte(column, value);
        case 'like':
          return acc.like(column, value);
        case 'ilike':
          return acc.ilike(column, value);
        case 'in':
          return acc.in(column, Array.isArray(value) ? value : [value]);
        case 'contains':
          return acc.contains(column, value);
        case 'is':
          return acc.is(column, value);
        case 'ov':
          return acc.overlaps(column, value);
        default:
          logger.warning('Unsupported Supabase filter operator, falling back to eq', {
            operator,
            column,
          });
          return acc.eq(column, value);
      }
    }, query);
  }

  applyOrder(query, order) {
    if (!order) {
      return query;
    }

    const orders = Array.isArray(order) ? order : [order];

    return orders.reduce((acc, current) => {
      if (!current?.column) {
        return acc;
      }

      return acc.order(current.column, {
        ascending: current.ascending !== false,
        nullsFirst: current.nullsFirst === true,
        foreignTable: current.foreignTable || undefined,
      });
    }, query);
  }

  handleResult(operation, table, result, options = {}) {
    const { data, error, count } = result || {};

    if (error) {
      const wrappedError = new Error(error.message || `Supabase ${operation} failed for ${table}`);
      wrappedError.code = error.code || error.status || error.__rawStatus;
      wrappedError.hint = error.hint;
      wrappedError.details = error.details;
      wrappedError.status = error.status || error.__rawStatus;
      throw wrappedError;
    }

    logger.info(`Supabase ${operation} successful`, {
      table,
      operation,
      count: Array.isArray(data) ? data.length : data ? 1 : 0,
      requestedCount: options.count === true ? count : undefined,
    });

    return data;
  }

  async select(table, options = {}) {
    const {
      columns = '*',
      filters = [],
      match = null,
      order = null,
      limit = null,
      offset = null,
      single = false,
      count = false,
    } = options;

    return this.withRetry(`select:${table}`, async () => {
      let query = this.client
        .from(table)
        .select(columns, { count: count ? 'exact' : undefined, head: false });

      if (match) {
        query = query.match(match);
      }

      query = this.applyFilters(query, filters);
      query = this.applyOrder(query, order);

      if (typeof limit === 'number' && typeof offset === 'number') {
        query = query.range(offset, offset + limit - 1);
      } else if (typeof limit === 'number') {
        query = query.limit(limit);
      }

      const result = single ? await query.maybeSingle() : await query;
      return this.handleResult('select', table, result, { count });
    });
  }

  async insert(table, payload, options = {}) {
    const dataToInsert = Array.isArray(payload) ? payload : [payload];

    return this.withRetry(`insert:${table}`, async () => {
      const query = this.client
        .from(table)
        .insert(dataToInsert, { returning: 'representation', defaultToNull: false });

      if (options.onConflict) {
        query.onConflict(options.onConflict);
      }

      if (options.count) {
        query.select('*', { count: 'exact' });
      } else {
        query.select();
      }

      const result = await query;
      return this.handleResult('insert', table, result, options);
    });
  }

  async update(table, payload, options = {}) {
    const { filters = [], single = false } = options;

    return this.withRetry(`update:${table}`, async () => {
      let query = this.client.from(table).update(payload, { returning: 'representation' });
      query = this.applyFilters(query, filters);

      query = query.select();

      const result = single ? await query.maybeSingle() : await query;
      return this.handleResult('update', table, result, options);
    });
  }

  async upsert(table, payload, options = {}) {
    const dataToUpsert = Array.isArray(payload) ? payload : [payload];

    return this.withRetry(`upsert:${table}`, async () => {
      let query = this.client
        .from(table)
        .upsert(dataToUpsert, {
          onConflict: options.onConflict,
          ignoreDuplicates: options.ignoreDuplicates === true,
          returning: 'representation',
        })
        .select();

      if (options.count) {
        query.select('*', { count: 'exact' });
      }

      const result = await query;
      return this.handleResult('upsert', table, result, options);
    });
  }

  async remove(table, options = {}) {
    const { filters = [], single = false } = options;

    return this.withRetry(`delete:${table}`, async () => {
      let query = this.client.from(table).delete();
      query = this.applyFilters(query, filters);

      query = query.select();
      const result = single ? await query.maybeSingle() : await query;
      return this.handleResult('delete', table, result, options);
    });
  }

  async rpc(fn, params = {}, options = {}) {
    return this.withRetry(`rpc:${fn}`, async () => {
      const result = await this.client.rpc(fn, params, {
        head: options.head || false,
        count: options.count ? 'exact' : undefined,
      });

      return this.handleResult('rpc', fn, result, options);
    });
  }
}

const supabaseService = new SupabaseService();
export default supabaseService;
